import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';

interface ChangeProvisionDatePopupProps {
    open: boolean;
    onClose: () => void;
    transaction: { id: string; date: string; description: string } | null;
    onConfirm: (guid: string, newDate: Dayjs) => void;
    isSaving?: boolean;
}

// Lets the user push a single provision occurrence's date forward or move it
// back - see the "Move" action menu item in transactions-table-virtual.tsx.
const ChangeProvisionDatePopup: React.FC<ChangeProvisionDatePopupProps> = ({
    open,
    onClose,
    transaction,
    onConfirm,
    isSaving = false,
}) => {
    const [newDate, setNewDate] = React.useState<Dayjs | null>(null);

    React.useEffect(() => {
        if (open && transaction) {
            setNewDate(dayjs(transaction.date));
        }
    }, [open, transaction]);

    const hasChanged = newDate && transaction && !newDate.isSame(dayjs(transaction.date), 'day');

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 20, pb: 1 }}>
                Move Provision
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)', mb: 2 }}>
                    {transaction?.description}
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="Provision date"
                        value={newDate}
                        onChange={(val) => setNewDate(val ? dayjs(val as unknown as Date) : null)}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                        disabled={isSaving}
                    />
                </LocalizationProvider>
                <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                        Pushing the date forward or moving it back only changes this occurrence - it doesn't affect the recurring template's future occurrences.
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 0, justifyContent: 'space-between' }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    disabled={isSaving}
                    sx={{ borderRadius: 2, textTransform: 'none', px: 1.5 }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={() => {
                        if (transaction && newDate) onConfirm(transaction.id, newDate);
                    }}
                    variant="contained"
                    disabled={!hasChanged || isSaving}
                    sx={{
                        background: 'var(--color-primary)',
                        borderRadius: 2,
                        textTransform: 'none',
                        color: '#fff',
                        px: 1.5,
                        '&:hover': { background: '#23615bff' },
                    }}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChangeProvisionDatePopup;
